import React, {useState, useEffect} from "react"
import {useAccessToken} from "../../../../services/auth"
import {toDollars} from "../../../../utilities/numbers"
import {Modal} from "../../../../components/Modals/Modal"
import {Button} from "../../../../components/Button/Button"
import {
	getAccountConfigOverrides,
	getAchOverrideId,
	getWireOverrideId,
	getCheckDepositOverrideId,
	getCheckPaymentOverrideId,
} from "../../../../resources/account"
import Admonition from "../../../../components/Admonitions/Admonition"
import {getTodayDateString} from "../../../../utilities/helpers"
import {
	EditMode,
	limitTypeLabels,
	formatCurrency,
	parseDollarInput,
	formatDateForInput,
	submitLimitOverride,
	isOneOf,
	achLimitTypes,
	wireLimitTypes,
	checkDepositLimitTypes,
	checkPaymentLimitTypes,
} from "./utils"
import type {LimitOverrideModalProps} from "./utils"
import {ConfirmationDialog} from "./ConfirmationDialog"
import {EditLimitSection} from "./EditLimitSection"
import {RevertLimitSection} from "./RevertLimitSection"

export function LimitOverrideModal({
	close,
	limitType,
	currentLimit,
	depositProductLimit,
	existingOverride,
	accountId,
	onSuccess,
}: LimitOverrideModalProps) {
	const accessToken = useAccessToken()
	const hasExistingOverride = !!existingOverride
	const limitLabel = limitTypeLabels[limitType]

	const [amount, setAmount] = useState(
		hasExistingOverride ? toDollars(existingOverride.amount) : toDollars(currentLimit)
	)
	const [endDate, setEndDate] = useState(formatDateForInput(existingOverride?.expiredAt))
	const [editMode, setEditMode] = useState<EditMode>(EditMode.Edit)

	const [overrideId, setOverrideId] = useState<string | undefined>(undefined)
	const [fetchingOverrideId, setFetchingOverrideId] = useState(true)

	useEffect(() => {
		setFetchingOverrideId(true)
		getAccountConfigOverrides(accessToken, accountId).then((result) => {
			if (result.isOk()) {
				let id: string | undefined
				if (isOneOf(limitType, achLimitTypes)) {
					id = getAchOverrideId(result.value)
				} else if (isOneOf(limitType, wireLimitTypes)) {
					id = getWireOverrideId(result.value)
				} else if (isOneOf(limitType, checkDepositLimitTypes)) {
					id = getCheckDepositOverrideId(result.value)
				} else if (isOneOf(limitType, checkPaymentLimitTypes)) {
					id = getCheckPaymentOverrideId(result.value)
				}
				if (id) {
					setOverrideId(id)
				}
			}
			setFetchingOverrideId(false)
		})
	}, [accessToken, accountId, limitType])

	const [showConfirmation, setShowConfirmation] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState<string | undefined>()

	const parsedAmount = parseDollarInput(amount)
	const isValidAmount = parsedAmount > 0
	const isReverting = hasExistingOverride && editMode === EditMode.Revert
	const todayDate = getTodayDateString()

	const handleAmountChange = (value: string | undefined) => {
		setAmount(value || "")
	}

	const handleSetLimit = () => {
		setShowConfirmation(true)
		setSubmitError(undefined)
	}

	const handleConfirm = async () => {
		setIsSubmitting(true)
		setSubmitError(undefined)

		try {
			const result = await submitLimitOverride({
				accessToken,
				accountId,
				limitType,
				amount: parsedAmount,
				endDate: endDate || undefined,
				overrideId,
				isReverting,
			})

			if (result.ok) {
				onSuccess()
				close()
			} else {
				setSubmitError(result.error)
			}
		} catch (err: any) {
			setSubmitError(err?.message || "An error occurred while saving the limit override.")
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleBack = () => {
		setShowConfirmation(false)
	}

	if (showConfirmation) {
		return (
			<ConfirmationDialog
				limitLabel={limitLabel}
				close={close}
				onConfirm={handleConfirm}
				onBack={handleBack}
				isReverting={isReverting}
				toAmount={isReverting ? depositProductLimit : parsedAmount}
				endDate={endDate}
				currentLimit={currentLimit}
				isLoading={isSubmitting}
				error={submitError}
			/>
		)
	}

	const formattedLimit = <strong>{formatCurrency(depositProductLimit)}</strong>
	const admonitionMessage = isReverting ? (
		<>This will revert the limit back to the default deposit product limit of {formattedLimit}.</>
	) : (
		<>This will override the default limit of {formattedLimit} set in the deposit product.</>
	)

	return (
		<Modal close={close} classname="limit-override-modal" enableClickAway={false} blockBodyScroll>
			<h3 className="modal-card-title">
				{hasExistingOverride ? "Edit Limit Override For " : "Create Limit Override For "}
				{limitLabel.category} {limitLabel.name}
			</h3>

			<EditLimitSection
				hasExistingOverride={hasExistingOverride}
				editMode={editMode}
				setEditMode={setEditMode}
				amount={amount}
				onAmountChange={handleAmountChange}
				endDate={endDate}
				onEndDateChange={setEndDate}
				todayDate={todayDate}
			/>

			{hasExistingOverride && (
				<RevertLimitSection editMode={editMode} setEditMode={setEditMode} depositProductLimit={depositProductLimit} />
			)}

			<Admonition type="is-warning" message={admonitionMessage} title="Note" />

			<footer className="limit-override-footer">
				<Button type="button" variant="flat" onClick={close}>
					Cancel
				</Button>
				<Button
					type="button"
					className={`button`}
					onClick={handleSetLimit}
					disabled={fetchingOverrideId || (!isReverting && !isValidAmount)}
					loading={fetchingOverrideId}
				>
					Set Limit
				</Button>
			</footer>
		</Modal>
	)
}
